package com.solevo.dist.components.dialogs

import androidx.compose.runtime.*
import com.solevo.dist.theme.SLColors
import com.solevo.libraries.entities.country.RemoteCountryService
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input

@Composable
fun CountryDialog(
    current: RemoteCountryService.CountryPayload.PhoneCallingCode?,
    countries: List<RemoteCountryService.CountryPayload.PhoneCallingCode>,
    onConfirm: (RemoteCountryService.CountryPayload.PhoneCallingCode?) -> Unit,
    onDismiss: () -> Unit
) {
    var query by remember { mutableStateOf("") }
    var selected by remember(current) { mutableStateOf(current) }

    // Overlay
    Box(
        Modifier
            .position(Position.Fixed)
            .left(0.px).top(0.px)
            .width(100.vw).height(100.vh)
            .backgroundColor(rgba(0, 0, 0, 0.35))
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.Center)
            .alignItems(AlignItems.Center)
            .zIndex(9999)
    ) {
        // Card
        Box(
            Modifier
                .backgroundColor(SLColors.White.rgb)
                .borderRadius(24.px)
                .padding(24.px)
                .minWidth(360.px)
                .maxWidth(520.px)
        ) {
            Column(Modifier.gap(16.px).width(100.percent)) {

                // HEADER
                CountryDialogHeader(
                    title = "Pick a Country",
                    query = query,
                    onQueryChange = { query = it }
                )

                // DATA PREP
                val allSorted = remember(countries) {
                    countries.sortedWith(
                        compareBy<RemoteCountryService.CountryPayload.PhoneCallingCode> { it.name.lowercase() }
                            .thenBy { it.callingCode }
                    )
                }
                val filtered = remember(query, allSorted) {
                    val q = query.trim().lowercase()
                    if (q.isEmpty()) allSorted
                    else allSorted.filter { c ->
                        val iso2 = c.isoCode.twoCharacters.lowercase()
                        val iso3 = c.isoCode.threeCharacters.lowercase()
                        c.name.lowercase().contains(q) ||
                                iso2.contains(q) || iso3.contains(q) ||
                                c.callingCode.lowercase().contains(q)
                    }
                }

                // LIST
                CountryDialogList(
                    items = filtered,
                    selected = selected,
                    onSelect = { selected = it }
                )

                // FOOTER
                CountryDialogFooter(
                    onCancel = onDismiss,
                    onConfirm = { onConfirm(selected) }
                )
            }
        }
    }
}

/* ========================= HEADER ========================= */

@Composable
private fun CountryDialogHeader(
    title: String,
    query: String,
    onQueryChange: (String) -> Unit
) {
    Column(Modifier.width(100.percent).gap(12.px)) {
        SpanText(
            title,
            modifier = Modifier.fontSize(28.px).fontWeight(700)
        )

        Input(type = InputType.Text, attrs = {
            value(query)
            onInput { ev -> onQueryChange(ev.value) }
            style {
                width(100.percent)
                padding(14.px, 18.px)
                backgroundColor(rgb(245, 246, 248))
                borderRadius(28.px)
                border(1.px, LineStyle.Solid, rgb(234, 236, 240))
                fontSize(14.px)
                outline("none")
            }
            attr("placeholder", "Search")
        })
    }
}

/* ========================= LIST ========================= */

@Composable
private fun CountryDialogList(
    items: List<RemoteCountryService.CountryPayload.PhoneCallingCode>,
    selected: RemoteCountryService.CountryPayload.PhoneCallingCode?,
    onSelect: (RemoteCountryService.CountryPayload.PhoneCallingCode) -> Unit
) {
    Box(
        Modifier
            .maxHeight(380.px)
            .width(100.percent)
            .styleModifier { overflowY("auto") }
            .borderRadius(12.px)
    ) {
        Column(Modifier.width(100.percent).gap(6.px)) {
            items.forEach { c ->
                val isSelected =
                    selected?.isoCode?.twoCharacters?.equals(c.isoCode.twoCharacters, ignoreCase = true) == true

                Row(
                    Modifier
                        .width(100.percent)
                        .padding(12.px, 10.px)
                        .borderRadius(12.px)
                        .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                        .onClick { onSelect(c) }
                        .alignItems(AlignItems.Center)
                ) {

                    Row(
                        Modifier
                            .flex(1)
                            .gap(12.px)
                            .alignItems(AlignItems.Center)
                    ) {
                        Img(
                            src = getFlagImageUrl(c.isoCode.twoCharacters),
                            attrs = Modifier
                                .size(24.px, 18.px)
                                .borderRadius(4.px)
                                .toAttrs {
                                    alt("${c.name} flag")
                                }
                        )

                        SpanText(
                            text = "${c.callingCode} (${c.name})",
                            modifier = Modifier.fontSize(18.px).color(rgb(20, 23, 27))
                        )
                    }

                    if (isSelected) {
                        Box(
                            Modifier
                                .size(24.px)
                                .borderRadius(50.percent)
                                .backgroundColor(rgb(180, 180, 180)) // svetlo siva
                                .display(DisplayStyle.Flex)
                                .justifyContent(JustifyContent.Center)
                                .alignItems(AlignItems.Center)
                        ) { SpanText("✓", modifier = Modifier.color(Colors.White).fontSize(14.px)) }
                    } else {
                        Box(
                            Modifier
                                .size(24.px)
                                .borderRadius(50.percent)
                                .border(2.px, LineStyle.Solid, rgb(228, 231, 235))
                        )
                    }
                }
            }
        }
    }
}

/* ========================= FOOTER ========================= */

@Composable
private fun CountryDialogFooter(
    onCancel: () -> Unit,
    onConfirm: () -> Unit
) {
    Row(
        Modifier
            .margin(top = 8.px)
            .width(100.percent)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
    ) {
        Button(
            onClick = { onCancel() },
            modifier = Modifier
                .backgroundColor(Colors.Transparent)
                .color(rgb(120, 124, 130))
                .fontWeight(600)
        ) { SpanText("Cancel") }

        Button(
            onClick = { onConfirm() },
            modifier = Modifier
                .backgroundColor(Colors.Transparent)
                .color(rgb(35, 131, 255))
                .fontWeight(700)
        ) { SpanText("Confirm") }
    }
}

/* ========================= UTIL ========================= */

/** ISO-2 -> flag image URL using CDN. Works reliably across all platforms including Windows. */
private fun getFlagImageUrl(alpha2: String): String {
    val isoLower = alpha2.take(2).lowercase()
    if (isoLower.length != 2) return ""
    // Using flagcdn.com CDN - reliable and works on all platforms
    return "https://flagcdn.com/w40/${isoLower}.png"
}